import { useState } from 'react';
import { Mail, UserPlus, UserRound } from 'lucide-react';
import { api, type Session } from '../api';
import { Button } from '../components/ui/button';

/** GET /app-api/mail/senders (#331): held email from unknown senders, and new senders waiting to become contacts. */
export interface HeldEmail {
  id: string; revision: number; from: string; subject: string; receivedAt: string; excerpt: string; why: string;
  suggestion: { customerId: string; name: string; evidence: string[]; source: 'model' | 'match' } | null;
  candidates: { customerId: string; name: string; score: number; evidence: string[] }[];
}
export interface ContactProposal {
  id: string; revision: number; customerId: string; customerName: string; email: string; name: string; title: string | null;
  phone: string | null; evidence: string; intakeId: string; subject: string; createdAt: string; question: string;
}
export interface SenderWork { emails: HeldEmail[]; proposals: ContactProposal[] }

/**
 * The Inbox's "Who is this?" filter. An email from someone not on file waits here with the customer it looks like and why;
 * Confirm links it (and remembers the address), Pick another links it elsewhere, Not a customer ignores it. A new sender at a
 * known customer waits as a contact to approve; approving handles their held email.
 */
export function SenderPanel({ session, work, customers, onChanged }: {
  session: Session; work: SenderWork; customers: { id: string; name: string }[]; onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false); const [error, setError] = useState(''); const [notice, setNotice] = useState('');
  const [picked, setPicked] = useState<Record<string, string>>({});
  const canDecide = session.actor.roles.some(r => ['owner', 'approver'].includes(r));
  async function run(work: () => Promise<unknown>, done: string) {
    setBusy(true); setError(''); setNotice('');
    try { await work(); setNotice(done); await onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Please try again.'); } finally { setBusy(false); }
  }
  const link = (item: HeldEmail, customerId: string) => run(() => api(`/app-api/mail/${item.id}/link`, { revision: item.revision, customerId }),
    'Linked. Carol now reads it as that customer\'s email; a new sender is proposed as a contact first.');
  const notCustomer = (item: HeldEmail) => run(() => api(`/app-api/mail/${item.id}/not-customer`, { revision: item.revision }), 'Marked not a customer.');
  const decide = (p: ContactProposal, decision: 'approve' | 'reject') => run(
    () => api(`/app-api/contact-proposals/${encodeURIComponent(p.id)}/decisions`, { revision: p.revision, decision }),
    decision === 'approve' ? `${p.name} is now a contact. Their email is handled now.` : 'Not added.');
  const empty = !work.emails.length && !work.proposals.length;
  return <div className="queue-items" aria-label="Who is this">
    {error && <div className="alert error" role="alert">{error}</div>}
    {notice && <div className="alert notice" role="status">{notice}</div>}
    {work.proposals.map(p => <div key={p.id} className="classification-note" aria-label={`New contact ${p.name}`}>
      <span className="eyebrow"><UserPlus size={14} aria-hidden /> NEW CONTACT</span>
      <p><strong>{p.question}</strong><br />{p.email}{p.phone ? ` · ${p.phone}` : ''}</p>
      <p className="muted">{p.evidence}. From “{p.subject}”.</p>
      <p className="muted">Nothing goes to this address until you approve.</p>
      {canDecide && <div className="resolution-buttons">
        <Button disabled={busy} onClick={() => void decide(p, 'approve')}>Add contact</Button>
        <Button variant="ghost" disabled={busy} onClick={() => void decide(p, 'reject')}>Don’t add</Button></div>}
    </div>)}
    {work.emails.map(item => {
      const choice = picked[item.id] ?? '';
      return <div key={item.id} className="classification-note" aria-label={`Held email ${item.subject}`}>
        <span className="eyebrow"><Mail size={14} aria-hidden /> WHO IS THIS?</span>
        <p><strong>{item.subject}</strong><br />From {item.from}</p>
        {item.excerpt && <p className="muted">{item.excerpt}</p>}
        <p>{item.why}</p>
        {item.suggestion ? <>
          <p><UserRound size={14} aria-hidden /> <strong>Looks like {item.suggestion.name}</strong></p>
          <ul>{item.suggestion.evidence.map(e => <li key={e} className="muted">{e}</li>)}</ul>
        </> : <p className="muted">Nothing in the email points to a customer.</p>}
        {canDecide && <div className="resolution-buttons">
          {item.suggestion && <Button disabled={busy} onClick={() => void link(item, item.suggestion!.customerId)}>Confirm</Button>}
          <label>Pick another<select value={choice} onChange={e => setPicked(s => ({ ...s, [item.id]: e.target.value }))}>
            <option value="">Choose a customer</option>
            {item.candidates.filter(c => c.customerId !== item.suggestion?.customerId).map(c => <option key={c.customerId} value={c.customerId}>{c.name} (suggested)</option>)}
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
          <Button variant="outline" disabled={busy || !choice} onClick={() => void link(item, choice)}>Link</Button>
          <Button variant="ghost" disabled={busy} onClick={() => void notCustomer(item)}>Not a customer</Button>
        </div>}
      </div>;
    })}
    {empty && <div className="queue-empty"><UserRound size={28} /><strong>Everyone is accounted for</strong>
      <p>Email from someone not on file, and new contacts to approve, appear here.</p></div>}
  </div>;
}
